import { useState } from "react";
import { RefreshCw, Check, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { RenderComparison } from "./RenderComparison";
import { RenderOptions } from "./RenderOptions";
import { RegenControls } from "./RegenControls";
import { TraitToggles } from "./TraitToggles";
import { StylePicker } from "./StylePicker";
import { DuckHelper } from "./DuckHelper";
import { SubjectKindChips } from "./SubjectKindChips";
import { errorText, frontImage } from "../lib/api";
import { BodyShapePicker } from "./BodyShapePicker";
import { useSubject } from "../lib/useSubject";
import { toast } from "sonner";

// Turns dropped/added-trait chips into a render correction note so the sculptor reflects the customer's choices.
const buildNote = ({ dropped, added }, userNote = "") => {
  const parts = [];
  if (dropped.length) parts.push(`Do NOT give the duck these features, even though the person has them: ${dropped.join(", ")}.`);
  if (added.length) parts.push(`Also add these fun features to the duck even if the person does not have them: ${added.join(", ")}.`);
  if (parts.length) parts.push("Keep every other feature and the outfit exactly the same.");
  if (userNote) parts.push(userNote);
  return parts.join(" ");
};

export const FacePreviewApprovalCard = ({ creation, onApprove, onRegenerate, onVariations, onSelect, onGenerateStyles, onSelectStyle, onBack, onUpdate, busy }) => {
  const [style, setStyle] = useState(creation.style || "classic");
  const [traits, setTraits] = useState({ dropped: [], added: [] });
  const [recommended, setRecommended] = useState(creation.recommended_style || null);
  const { subject, busy: subjectBusy, patch } = useSubject(creation.id);
  const renders = creation.renders?.length ? creation.renders : [frontImage(creation)].filter(Boolean);
  const selected = creation.render_index ?? 0;

  const changeSubject = async (body, key) => {
    try {
      const { creation: fresh } = await patch(body, key);
      onUpdate?.(fresh);
    } catch (e) {
      toast.error(errorText(e, "Could not change that."));
    }
  };

  return (
    <div data-testid="face-preview-card" className="animate-pop">
      <RenderComparison creation={creation} renders={renders} selected={selected} regenerating={busy === "regenerate"} />

      <div className="mt-5 grid md:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="md:order-1 order-2">
          <DuckHelper
            creation={creation}
            applyOptions={[{ id: "render", label: "Regenerate render with these notes" }]}
            applyBusy={busy === "regenerate" ? "render" : null}
            onApply={(_id, note) => onRegenerate(style, creation.figure || "full", buildNote(traits, note))}
          />
        </div>
        <div className="md:order-2 order-1">
          <RenderOptions renders={renders} selected={selected} busy={busy} varying={busy === "variations"} onSelect={onSelect} onVariations={onVariations} />
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-border bg-card/60 p-5 sm:p-6 space-y-5">
        <SubjectKindChips subject={subject} busy={subjectBusy} onChange={(kind) => changeSubject({ input_kind: kind }, "input_kind")} />
        {subject?.body_shape_applies && (
          <div className="border-t border-border pt-5">
            <BodyShapePicker subject={subject} busy={subjectBusy} onChange={(shape) => changeSubject({ body_shape: shape }, "body_shape")} />
          </div>
        )}
        <div className="border-t border-border pt-5">
          <RegenControls style={style} onStyle={setStyle} />
        </div>
        <TraitToggles creationId={creation.id} value={traits} onChange={setTraits} onRecommend={setRecommended} disabled={!!busy} />
        <div className="border-t border-border pt-5">
          <StylePicker
            creation={creation}
            onGenerate={() => onGenerateStyles(buildNote(traits))}
            onSelect={onSelectStyle}
            recommended={recommended}
            disabled={!!busy}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Approving sends the selected render to the AI sculptor, which builds a solid 3D model.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" className="rounded-full gap-2 h-12" onClick={onBack} disabled={!!busy} data-testid="face-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button data-testid="face-regenerate-btn" variant="secondary" className="rounded-full gap-2 h-12 flex-1" onClick={() => onRegenerate(style, creation.figure || "full", buildNote(traits))} disabled={!!busy}>
            {busy === "regenerate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Regenerate render
          </Button>
          <Button data-testid="face-approve-btn" className="rounded-full gap-2 h-12 flex-1 font-bold duck-shadow" onClick={onApprove} disabled={!!busy}>
            {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Approve & Sculpt in 3D
          </Button>
        </div>
      </div>
    </div>
  );
};
